import { useEffect, useMemo, useRef, useState } from 'react';
import { CircleCheckIcon } from 'lucide-react';

import type { ProductCategory } from '@/api/hooks/queries/use-get-product-categories-query';
import diabetesDexcomG6Sensor from '@/assets/product-categories/diabetes/dexcom-g6-sensor.png';
import diabetesFreestyleLibre3 from '@/assets/product-categories/diabetes/freestyle-libre-3.png';
import diabetesLancet from '@/assets/product-categories/diabetes/lancet.png';
import accessibilityHospitalBed from '@/assets/product-categories/home-accessibility/hospital-bed.png';
import accessibilityOverbedTable from '@/assets/product-categories/home-accessibility/overbed-table.png';
import accessibilityRaisedToiletSeat from '@/assets/product-categories/home-accessibility/raised-toilet-seat.png';
import incontinenceDisposableUnderwear from '@/assets/product-categories/incontinence/disposable-underwear.png';
import incontinenceLinerPad from '@/assets/product-categories/incontinence/liner-pad.png';
import incontinenceUnderpad from '@/assets/product-categories/incontinence/underpad.png';
import mobilityRollator from '@/assets/product-categories/mobility/rollator.png';
import mobilityWalker from '@/assets/product-categories/mobility/walker.png';
import mobilityWheelchair from '@/assets/product-categories/mobility/wheelchair.png';
import { Heading1 } from '@/components/common/Heading1';
import { StickyFooterCta } from '@/components/common/StickyFooterCta';
import { FieldError } from '@/components/ui/field';
import { cn } from '@/lib/utils';

type CategoryConfig = {
  label: string;
  description: string;
  images: string[];
};

const CATEGORY_CONFIG: Record<string, CategoryConfig> = {
  mobility: {
    label: 'Mobility',
    description: 'Wheelchairs, walkers, canes, and rollators',
    images: [mobilityWheelchair, mobilityRollator, mobilityWalker],
  },
  incontinence: {
    label: 'Incontinence',
    description: 'Adult diapers, protective underwear, and pads',
    images: [incontinenceDisposableUnderwear, incontinenceLinerPad, incontinenceUnderpad],
  },
  diabetes: {
    label: 'Diabetes',
    description: 'Blood glucose monitors, test strips, and lancets',
    images: [diabetesDexcomG6Sensor, diabetesFreestyleLibre3, diabetesLancet],
  },
  'home-accessibility': {
    label: 'Home accessibility',
    description: 'Grab bars, shower chairs, hospital beds',
    images: [accessibilityHospitalBed, accessibilityRaisedToiletSeat, accessibilityOverbedTable],
  },
};

const CATEGORY_ORDER = Object.keys(CATEGORY_CONFIG);

function getCategoryKey(category: Pick<ProductCategory, 'label'>) {
  return category.label.toLowerCase().replace(/&/g, 'and').replace(/\s+/g, '-');
}

function getCategoryConfig(category: Pick<ProductCategory, 'label'>): CategoryConfig | undefined {
  return CATEGORY_CONFIG[getCategoryKey(category)];
}

export function isSupportedProductCategory(category: ProductCategory) {
  return getCategoryConfig(category) !== undefined;
}

export function isSupportedCategoryName(categoryName: string) {
  return getCategoryConfig({ label: categoryName }) !== undefined;
}

type ProductCategoriesProps = {
  categories: ProductCategory[];
  referrerCategoryId?: string;
  selectedCategoryIds: string[];
  onSubmit: (categoryIds: string[]) => void;
};

export function ProductCategories({
  categories,
  referrerCategoryId,
  selectedCategoryIds,
  onSubmit,
}: ProductCategoriesProps) {
  const visibleCategoryIds = useMemo(
    () => new Set(categories.map((category) => category.uuid)),
    [categories]
  );
  const orderedCategories = useMemo(
    () =>
      [...categories].sort((first, second) => {
        if (referrerCategoryId) {
          if (first.uuid === referrerCategoryId) return -1;
          if (second.uuid === referrerCategoryId) return 1;
        }

        return (
          CATEGORY_ORDER.indexOf(getCategoryKey(first)) -
          CATEGORY_ORDER.indexOf(getCategoryKey(second))
        );
      }),
    [categories, referrerCategoryId]
  );
  const [selectedIds, setSelectedIds] = useState(() => {
    const initialSelectedIds = selectedCategoryIds.filter((categoryId) =>
      visibleCategoryIds.has(categoryId)
    );

    if (referrerCategoryId && visibleCategoryIds.has(referrerCategoryId)) {
      return Array.from(new Set([...initialSelectedIds, referrerCategoryId]));
    }

    return initialSelectedIds;
  });
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const hasReferrerCategory = Boolean(
    referrerCategoryId && visibleCategoryIds.has(referrerCategoryId)
  );

  function toggleCategory(categoryId: string) {
    setError(null);
    setSelectedIds((current) =>
      current.includes(categoryId) && categoryId !== referrerCategoryId
        ? current.filter((selectedId) => selectedId !== categoryId)
        : Array.from(new Set([...current, categoryId]))
    );
  }

  function submitSelection() {
    if (!hasReferrerCategory && selectedIds.length === 0) {
      setError('Please select at least one category');
      return;
    }

    onSubmit(selectedIds.filter((categoryId) => visibleCategoryIds.has(categoryId)));
  }

  const buttonLabel =
    selectedIds.length > 0
      ? `Continue with ${selectedIds.length} ${
          selectedIds.length === 1 ? 'category' : 'categories'
        } selected`
      : 'Continue';

  useEffect(() => {
    if (error) {
      errorRef.current?.focus();
    }
  }, [error]);

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-5 pb-28 sm:gap-6 lg:max-w-4xl xl:max-w-5xl">
      {hasReferrerCategory ? (
        <div>
          <Heading1 className="mb-1 text-[32px] leading-[1.15] sm:mb-1 sm:text-[32px]">
            Do you need any other types of supplies?
          </Heading1>
          <p className="text-charcoal-900 text-base leading-[1.4]">
            They could also be covered by insurance.
          </p>
        </div>
      ) : (
        <>
          <CategorySelectionIntro />
          <p className="text-charcoal-900 text-base leading-[1.4]">
            Select from the categories below:
          </p>
        </>
      )}

      <div
        className="flex flex-col gap-4 sm:grid sm:grid-cols-2"
        role="group"
        aria-label="Medical supply categories"
      >
        {orderedCategories.map((category) => (
          <ProductCategoryCard
            key={category.uuid}
            category={category}
            isSelected={selectedIds.includes(category.uuid)}
            onToggle={toggleCategory}
          />
        ))}
      </div>

      {error ? (
        <FieldError ref={errorRef} tabIndex={-1} className="scroll-mb-28 outline-none">
          {error}
        </FieldError>
      ) : null}

      <StickyFooterCta
        onClick={submitSelection}
        aria-label={buttonLabel}
        contentClassName="max-w-sm"
        className="sm:h-12 sm:text-base"
      >
        Continue
      </StickyFooterCta>
    </section>
  );
}

function CategorySelectionIntro() {
  return (
    <div className="shadow-card flex flex-col gap-4 rounded-xl border border-neutral-300 bg-white px-4 py-7">
      <div className="flex items-center gap-2">
        <CircleCheckIcon
          aria-hidden="true"
          className="fill-spruce-500 size-4 shrink-0 text-white"
        />
        <span className="text-charcoal-900 text-sm leading-none font-medium">
          Trusted by 50,000+ patients
        </span>
      </div>

      <Heading1 className="mb-0 text-[32px] leading-[1.15] sm:mb-0 sm:text-[32px]">
        You’re minutes away from covered supplies.
      </Heading1>

      <p className="text-charcoal-900 text-base leading-[1.4]">
        Over the next few minutes we’ll confirm your insurance eligibility and get you scheduled
        with a telehealth provider if you need a prescription.
      </p>
    </div>
  );
}

type ProductCategoryCardProps = {
  category: ProductCategory;
  isSelected: boolean;
  onToggle: (categoryId: string) => void;
};

function ProductCategoryCard({ category, isSelected, onToggle }: ProductCategoryCardProps) {
  const config = getCategoryConfig(category);
  const displayLabel = config?.label ?? category.label;
  const displayDescription = config?.description ?? category.description;
  const images = config?.images ?? [];

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={() => onToggle(category.uuid)}
      className={cn(
        'shadow-card focus-visible:ring-ring flex w-full flex-col gap-3 overflow-hidden rounded-xl border-2 bg-white p-3 text-left focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        isSelected
          ? 'border-neutral-600 bg-neutral-600'
          : 'border-neutral-300 hover:border-neutral-600'
      )}
    >
      <div className="grid grid-cols-3 gap-1">
        {[0, 1, 2].map((slot) => (
          <div
            key={slot}
            className="bg-greige-100 flex aspect-square min-w-0 items-center justify-center overflow-hidden rounded-lg"
          >
            {images[slot] ? (
              <img src={images[slot]} alt="" aria-hidden className="h-full w-full object-cover" />
            ) : null}
          </div>
        ))}
      </div>

      <div>
        <span
          className={cn('block text-lg leading-none font-semibold', isSelected && 'text-white')}
        >
          {displayLabel}
        </span>
        {displayDescription ? (
          <span
            className={cn(
              'mt-1 block text-sm leading-[1.4]',
              isSelected ? 'text-white' : 'text-neutral-600'
            )}
          >
            {displayDescription}
          </span>
        ) : null}
      </div>
    </button>
  );
}
